import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  FaClipboardCheck,
  FaKey,
  FaChartBar,
  FaQuestionCircle, FaHandshake,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import BarraMenu from "../../components/BarraMenu";
import { businessThemes } from "../../theme/businessThemes";
import "./styles.css";

// Constants
const AUTHORIZED_PROFILES = ["Administrador", "Evaluador", "Evaluador Externo"];
const ADMIN_PROFILES = ["Administrador"];
const EVALUATOR_PROFILES = ["Evaluador"];

const NAVIGATION_ITEMS = [
  {
    icon: FaHandshake,
    text: "Usuarios",
    path: "/gestionColaboradores",
    requiresAuth: true,
    allowedProfiles: ["Administrador"],
    description: "Creación y edición de usuarios",
  },
  {
    icon: FaClipboardCheck,
    text: "Evaluaciones",
    path: "/realizarEvaluaciones",
    requiresAuth: true,
    allowedProfiles: ["Evaluador", "Evaluador Externo"],
    description: "Realizar evaluación / retroalimentación",
  },
  {
    icon: FaChartBar,
    text: "Reportes",
    path: "/reporteEvaluaciones",
    requiresAuth: true,
    allowedProfiles: ["Evaluador", "Evaluador Externo"],
    description: "Reportes de evaluaciones realizadas",
  },
  {
    icon: FaKey,
    text: "Cambiar Contraseñas",
    path: "/admonUsuariosExternos",
    requiresAuth: true,
    allowedProfiles: ["Administrador"],
    description: "Restaurar contraseñas",
  },
];

const Tooltip = ({ text, theme }: { text: string; theme: any; }) => (
  <div className="tooltip" style={{ backgroundColor: theme.tooltipBackground }}> {text}</div>
);

const NavigationCard = ({
  icon: Icon,
  text,
  path,
  isDisabled,
  description,
  theme,
}: {
  icon: any;
  text: string;
  path: string;
  isDisabled: boolean;
  description: string;
  theme: any;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <motion.div
      whileHover={{ scale: isDisabled ? 1 : 1.05 }}
      whileTap={{ scale: isDisabled ? 1 : 0.95 }}
    >
      <Link
        className={`navigation-card ${isDisabled ? "disabled" : ""}`}
        to={isDisabled ? "#" : path}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Icon className="navigation-icon"  style={{ color: showTooltip ? theme.iconHoverColor : theme.iconColor }} />
        <span className="navigation-text">{text}</span>
        {showTooltip && <Tooltip text={description} theme={theme} />}
        {isDisabled && <FaQuestionCircle className="restricted-icon" />}
      </Link>
    </motion.div>
  );
};
/* pie de pagina */
const Footer = () => (
  <motion.footer
    className="footer"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 0.5 }}
  >
    <div className="text-center">
      <span style={{ color: "#003366", fontWeight: "normal" }}>
        © Digital ProfIT {new Date().getFullYear()}
      </span>
    </div>
  </motion.footer>
);
/* pie de pagina */
const PageLandingPage: React.FC = () => {
  const { profile, first_name, idbusiness_group  } = useSelector((state: any) => state.emp);
  const businessGroupId = idbusiness_group || 1;
  const theme = businessThemes[businessGroupId] || businessThemes[1];
  const isAuthorizedUser = AUTHORIZED_PROFILES.includes(profile);

  // Estado para controlar la visibilidad del h1
  const [showWelcomeTitle, setShowWelcomeTitle] = useState(true); // Estado para controlar la visibilidad del h1

  useEffect(() => {
    // Timer para desaparaecer el h1
    const timer = setTimeout(() => {
      setShowWelcomeTitle(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (businessGroupId === 1) {
      document.body.dataset.client = "digital";
    }
    return () => {
      delete document.body.dataset.client;
    };
  }, [businessGroupId]);

  // Mensaje para usuarios sin acceso
  const UnauthorizedMessage = () => (
    <motion.div
      className="alert alert-warning text-center"
      style={{ maxWidth: "600px", margin: "2rem auto" }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <FaQuestionCircle size={50} className="mb-3" />
      <h4>Acceso Restringido</h4>
      <p className="mb-0">
        Su perfil actual <strong>({profile})</strong> no tiene permisos para acceder a esta aplicación.
        Por favor, contacte al administrador del sistema.
      </p>
    </motion.div>
  );

  return (
    <div className={businessGroupId === 1 ? "home-outer" : "home-outer-plain"}>
    <div className="container text-center home-container">
      <BarraMenu />
      <motion.div /* Animación cuadro completo de entrada */
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <div className="welcome-section">
          <AnimatePresence>
            {showWelcomeTitle ? (
              <motion.h1
                className="welcome-title"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5 }}
              >
                ¡Bienvenid@, {first_name}!
              </motion.h1>
            ) : null}
          </AnimatePresence>
          <h4 className="welcome-subtitle" style={{ marginTop: "10px" }}>
            Sistema de evaluación de desempeño
          </h4>
        </div>

        {!isAuthorizedUser ? (
          <UnauthorizedMessage />
        ) : (
          <div className="navigation-grid">
            {NAVIGATION_ITEMS.filter(
              (item) => item.allowedProfiles.includes(profile)
            ).map((item, index) => (
              <NavigationCard
                key={index}
                icon={item.icon}
                text={item.text}
                path={item.path}
                isDisabled={false}
                description={item.description}
                theme={theme}
              />
            ))}
          </div>
        )}

        <motion.img
          src={require("./digitalProf-it.png")}
          alt="Logo"
          className={`floating-main_image "logo-spin" : ""}`}
          style={{
            marginTop: "280px",

            marginBottom: "1px",
            margin: "auto",
          }}
        />
      </motion.div>

      <Footer />
    </div>
    </div>
  );
};

export default PageLandingPage;